import { useState } from 'react'
import { lineFeatureFlags } from '../../../config/lineFeatureFlags'
import { useClientPreferences } from '../../../context/useClientPreferences'
import { useIsSystemAdmin } from '../../../hooks/useIsSystemAdmin'
import { useNls } from '../../../nls/nlsProvider'
import { isLineSignalCaptureEnabled, setLineSignalCaptureEnabled } from '../../../services/lineLearningSignals'
import {
  Pressable,
  SettingsReadOnlyRow,
  SettingsRow,
  SettingsSection,
  StatusChip,
  Toggle,
  useInstantSettingRows,
} from '../../Common'
import { AiUserRulesPanel } from '../AiUserRulesPanel'
import { LineSuggestionsPanel } from '../LineSuggestionsPanel'

export const AiMagicPanel = () => {
  const { preferences, updatePreferences } = useClientPreferences()
  const { t } = useNls()
  const isSystemAdmin = useIsSystemAdmin()
  const [learningSignalCaptureEnabled, setLearningSignalCaptureEnabled] = useState(isLineSignalCaptureEnabled)
  const instantRows = useInstantSettingRows()

  return (
    <div className="skSectionStack">
      <SettingsSection title={t('NLSSettingsSectionAnswersAndDrafts')}>
        <SettingsReadOnlyRow
          label={t('NLSSettingsLabelSuggestedAnswers')}
          hint={t('NLSSettingsHintSuggestedAnswers')}
          value={null}
          chip={
            <StatusChip size="sm" tone="info">
              {t('NLSSettingsBadgeSelectSubmitLocked')}
            </StatusChip>
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelDraftReadyNotifications')}
          hint={t('NLSSettingsHintDraftReadyNotifications')}
          status={instantRows.statusFor('aiMagicDraftReadyNotifications')}
          control={
            <Toggle
              checked={preferences.aiMagicDraftReadyNotifications}
              onChange={(next) =>
                void instantRows.run('aiMagicDraftReadyNotifications', () =>
                  updatePreferences({ aiMagicDraftReadyNotifications: next }),
                )
              }
              aria-label={t('NLSSettingsLabelDraftReadyNotifications')}
            />
          }
        />

        <SettingsRow
          label={t('NLSSettingsLabelManageHandoff')}
          hint={t('NLSSettingsHintManageHandoff')}
          status={instantRows.statusFor('aiMagicAutoOpenManageDrafts')}
          control={
            <div className="settingsSegmented">
              <Pressable
                aria-pressed={!preferences.aiMagicAutoOpenManageDrafts}
                className={
                  !preferences.aiMagicAutoOpenManageDrafts ? 'isActive' : ''
                }
                onClick={() =>
                  void instantRows.run('aiMagicAutoOpenManageDrafts', () =>
                    updatePreferences({ aiMagicAutoOpenManageDrafts: false }),
                  )
                }
              >
                {t('NLSSettingsActionManual')}
              </Pressable>
              <Pressable
                aria-pressed={Boolean(preferences.aiMagicAutoOpenManageDrafts)}
                className={
                  preferences.aiMagicAutoOpenManageDrafts ? 'isActive' : ''
                }
                onClick={() =>
                  void instantRows.run('aiMagicAutoOpenManageDrafts', () =>
                    updatePreferences({ aiMagicAutoOpenManageDrafts: true }),
                  )
                }
              >
                {t('NLSSettingsActionAutoOpen')}
              </Pressable>
            </div>
          }
        />
      </SettingsSection>

      <SettingsSection title={t('NLSSettingsSectionLearning')}>
        <SettingsRow
          label={t('NLSSettingsLabelLineLearningSignals')}
          hint={t('NLSSettingsHintLineLearningSignals')}
          status={instantRows.statusFor('lineLearningSignals')}
          control={
            <Toggle
              checked={learningSignalCaptureEnabled}
              onChange={(next) => {
                void instantRows.run('lineLearningSignals', async () => {
                  await setLineSignalCaptureEnabled(next)
                  setLearningSignalCaptureEnabled(next)
                })
              }}
              aria-label={t('NLSSettingsLabelLineLearningSignals')}
            />
          }
        />

        {isSystemAdmin ? (
          <SettingsReadOnlyRow
            label={t('NLSSettingsLabelTestingMode')}
            hint={t('NLSSettingsHintHistoryLimitsBypassed')}
            value={t('NLSSettingsStatusHistoryLimitsBypassed')}
            chip={
              <StatusChip size="sm" tone="info">
                {t('NLSSettingsChipOwnerOnly')}
              </StatusChip>
            }
          />
        ) : null}
      </SettingsSection>

      <SettingsSection title={t('NLSSettingsSectionYourAiRules')}>
        <AiUserRulesPanel />
      </SettingsSection>

      {lineFeatureFlags.ambient ? (
        <SettingsSection title={t('NLSSettingsSectionLineSuggestions')}>
          <LineSuggestionsPanel />
        </SettingsSection>
      ) : null}
    </div>
  )
}
